// Cycle Count WebSocket event parser. Pure function — easy Vitest target;
// the useCycleCountSocket hook wraps it.

import type { CycleCountMetrics } from './metricsTypes';

export type CycleCountEvent =
  | { type: 'hello'; serverTime: string }
  | { type: 'count'; n: number; code: string }
  | { type: 'llm_result'; code: string; result: unknown }
  | { type: 'backpressure'; droppedSinceLast: number }
  | { type: 'progress'; framesReceived: number; framesProcessed: number }
  | { type: 'metrics'; snapshot: CycleCountMetrics };

export type ParseResult =
  | { ok: true; event: CycleCountEvent }
  | { ok: false; reason: string };

const KNOWN_TYPES = ['hello', 'count', 'llm_result', 'backpressure', 'progress', 'metrics'] as const;

export function parseCycleCountEvent(raw: string): ParseResult {
  let payload: unknown;
  try {
    payload = JSON.parse(raw);
  } catch {
    return { ok: false, reason: 'invalid-json' };
  }

  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) {
    return { ok: false, reason: 'not-object' };
  }

  const type = (payload as { type?: unknown }).type;
  if (typeof type !== 'string') {
    return { ok: false, reason: 'missing-type' };
  }

  if (!KNOWN_TYPES.includes(type as (typeof KNOWN_TYPES)[number])) {
    return { ok: false, reason: `unknown-type:${type}` };
  }

  // The `metrics` event carries a nested `snapshot` — validate its presence
  // so a server-shape drift surfaces here rather than as `undefined` values
  // silently rendered in MetricsPanel (todo 197).
  if (type === 'metrics') {
    const snapshot = (payload as { snapshot?: unknown }).snapshot;
    if (typeof snapshot !== 'object' || snapshot === null) {
      return { ok: false, reason: 'metrics-missing-snapshot' };
    }
  }

  return { ok: true, event: payload as CycleCountEvent };
}
